import React from "react";
import { InitialAppDataProvider } from "./providers/initial-app-data";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import PageContext from "./components/Common/PageContext";
import { createBrowserHistory } from "history";
import { DrawerMenu } from "./components/DrawerMenu/DrawerMenu";
import { setStateAsync } from "./util/stateHelper";
import { receiveStatus } from "./util/receiveStatus";
import { creditStatus } from "./util/creditRequestMapping";
import { receiveFailureReason, failureReasonsRequiringInterventionCsv } from "./util/receiveFailureReason";
import SnackbarAlert from "./components/Snackbar/SnackbarAlert";
import { snackBarAlerts } from "./util/snackBarAlert";
import { UserProvider } from "./providers/user";
import { AuthGate } from "./infrastructure/auth";
import { AppRouter } from "./AppRouter";
import { reactPlugin } from "./services";
import { AxiosInterceptors } from "./infrastructure/http/AxiosInterceptors";
import { displayTypes } from "./util/displayTypes";
import { JbAppBar } from "./components/AppBar/JbAppBar";

const browserHistory = createBrowserHistory({});

class App extends React.Component {
	constructor(props) {
		super(props);
		this.setStateAsync = setStateAsync.bind(this);
		this.state = {
			isOpenMenu: false,
			userLocation: 0,
			badge: {
				creditRequests: [],
				receives: [],
				receiveFailures: [],
				transfers: [],
			},
			hasRetrievedPrinters: false,
			hasRetrievedApplicationData: false,
			printers: [],
			productGroups: [],
			vendors: [],
			featureConfiguration: {
				arrivals: {},
				adhocCreditRequest: {},
				productLabelAdhocPrint: {},
				transfers: {},
				outstandingEdiInvoices: {},
				inboundLocationAssignment: {},
				productLabelAutoPrint: {},
				priorityStock: {},
				commercialAccountSelection: {},
				customerOrderAllocationAndAdvancement: {},
				skipCustomerOrderAllocation: {},
				partReceive: {},
				captureFreightCost: {},
			},
			locations: {
				authorised: {
					ids: [],
					primary: -1,
					list: [],
				},
				physical: {},
				default: 0,
				isLoaded: false,
				allLocations: [],
			},
			snackBarAlert: snackBarAlerts.closed,
			userInformation: {
				staffId: "",
				name: "",
				countryCode: "",
				clientIp: "",
			},
			putAwayLocations: new Set(),
			commercialAccounts: [],
			carriers: [],
		};
	}

	updateContext = (data, setStateDoneCallBack = null) => {
		this.setState({ ...this.state, ...data }, setStateDoneCallBack);
	};

	onMenuClick = () => {
		this.setState({ isOpenMenu: true });
	};
	onMenuItemSelected = (menuName) => {
		this.setState({ isOpenMenu: false });
		switch (menuName) {
			case "home":
				browserHistory.push("/");
				break;
			case "checkin":
				browserHistory.push("/checkin");
				break;
			case `receives${receiveStatus.arrived}`:
				browserHistory.push(
					`/receives?status=${receiveStatus.arrived}&displayType=${displayTypes.purchaseOrders}&t=${Date.now()}`
				);
				break;
			case `receives${receiveStatus.pendingReview}`:
				browserHistory.push(
					`/receives?status=${receiveStatus.pendingReview}&displayType=${displayTypes.purchaseOrders}&t=${Date.now()}`
				);
				break;
			case `receives${receiveStatus.reviewing}`:
				browserHistory.push(
					`/receives?status=${receiveStatus.reviewing}&displayType=${displayTypes.purchaseOrders}&t=${Date.now()}`
				);
				break;
			case `receives${receiveStatus.inProgress}`:
				browserHistory.push(
					`/receives?status=${receiveStatus.inProgress}&displayType=${displayTypes.purchaseOrders}&t=${Date.now()}`
				);
				break;
			case `receives${receiveStatus.pendingReceive}`:
				browserHistory.push(
					`/receives?status=${receiveStatus.pendingReceive}&displayType=${displayTypes.purchaseOrders}&t=${Date.now()}`
				);
				break;
			case `receives${receiveFailureReason.ediInvoiceMissing}`:
				browserHistory.push(
					`/receives?status=${receiveStatus.pendingFinalise}&failureReasons=${
						receiveFailureReason.ediInvoiceMissing
					}&displayType=${displayTypes.purchaseOrders}&t=${Date.now()}`
				);
				break;
			case `receives${receiveStatus.pendingFinalise}`:
				browserHistory.push(
					`/receives?status=${
						receiveStatus.pendingFinalise
					}&failureReasons=${failureReasonsRequiringInterventionCsv}&displayType=${
						displayTypes.purchaseOrders
					}&t=${Date.now()}`
				);
				break;
			case `credits${creditStatus.pendingReview}`:
				browserHistory.push(`/creditrequests?status=${creditStatus.pendingReview}`);
				break;
			case `credits${creditStatus.reviewing}`:
				browserHistory.push(`/creditrequests?status=${creditStatus.reviewing}`);
				break;
			case `credits${creditStatus.awaitingSupplier}`:
				browserHistory.push(`/creditrequests?status=${creditStatus.awaitingSupplier}`);
				break;
			case "creditRequestSearch":
				browserHistory.push("/creditrequests/search");
				break;
			case "adhocCredit":
				browserHistory.push("/creditrequests/adhoc");
				break;
			case "transfersIncoming":
				browserHistory.push(`/transfers/incoming?t=${Date.now()}`);
				break;
			case "transfersOutstandingVirtuals":
				browserHistory.push(`/transfers/outstandingVirtuals?t=${Date.now()}`);
				break;
			case "transfersContinueInProgress":
				browserHistory.push(
					`/receives?status=${receiveStatus.inProgress}&displayType=${displayTypes.transfers}&t=${Date.now()}`
				);
				break;
			case "transfersPendingReceive":
				browserHistory.push(
					`/receives?status=${receiveStatus.pendingReceive}&displayType=${displayTypes.transfers}&t=${Date.now()}`
				);
				break;
			case "settings":
				browserHistory.push("/settings");
				break;
			case "asn":
				browserHistory.push("/asn");
				break;
			case "labels":
				browserHistory.push("/labels");
				break;
			case "tagSheetLabels":
				browserHistory.push("/tag-sheet-labels");
				break;
			case "outstandingEdiInvoices":
				browserHistory.push("/outstandingediinvoices");
				break;
			default:
				break;
		}
	};

	OnDrawerClose = () => {
		this.setState({ isOpenMenu: false });
	};

	handleCloseSnackbarAlertAsync = async () => {
		const { snackBarAlert } = this.state;
		await this.setStateAsync({ snackBarAlert: { ...snackBarAlert, isOpen: false } });
	};

	render() {
		const { isOpenMenu } = this.state;
		return (
			<AuthGate>
				<UserProvider>
					<AppInsightsContext.Provider value={reactPlugin}>
						<PageContext.Provider value={[this.state, this.updateContext]}>
							<AxiosInterceptors>
								<InitialAppDataProvider>
									<JbAppBar {...this.props} onMenuClick={this.onMenuClick}>
										<AppRouter {...this.props} history={browserHistory} />
									</JbAppBar>
									<DrawerMenu
										onMenuItemSelected={this.onMenuItemSelected}
										OnDrawerClose={this.OnDrawerClose}
										isOpenMenu={isOpenMenu}
									/>
									<SnackbarAlert
										open={this.state.snackBarAlert.isOpen}
										message={this.state.snackBarAlert.message}
										severity={this.state.snackBarAlert.severity}
										onClose={this.handleCloseSnackbarAlertAsync}
									/>
								</InitialAppDataProvider>
							</AxiosInterceptors>
						</PageContext.Provider>
					</AppInsightsContext.Provider>
				</UserProvider>
			</AuthGate>
		);
	}
}
export default App;
